import React, { useState } from 'react';
import { ChevronDown, ChevronUp, ExternalLink, Download } from 'lucide-react';

const SectionTitle = ({ title, isExpanded, onClick }) => (
  <button
    onClick={onClick}
    className="flex justify-between items-center w-full p-4 bg-transparent hover:bg-gray-50 transition-colors"
  >
    <span className="font-semibold text-gray-900">{title}</span>
    {isExpanded ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
  </button>
);

const Section = ({ title, content, bgColor, defaultExpanded = false }) => {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);
  
  if (!content || (Array.isArray(content) && content.length === 0)) {
    return null;
  }

  return (
    <div className={`border border-gray-200 rounded-lg overflow-hidden ${bgColor}`}>
      <SectionTitle
        title={title}
        isExpanded={isExpanded}
        onClick={() => setIsExpanded(!isExpanded)}
      />
      {isExpanded && (
        <div className="p-4 border-t border-gray-200">
          <div className="text-sm text-gray-700 whitespace-pre-line">
            {content}
          </div>
        </div>
      )}
    </div>
  );
};

const CaseCard = ({ caseItem, onViewCase, onDownloadCase }) => {
  const formatCaseSummary = (summary) => {
    return summary?.trim() || "No summary available";
  };

  const formatLegalIssues = (issues) => {
    if (!issues || issues.length === 0) return null;
    return issues.map((issue, i) => (
      `Issue ${i + 1}:\n` +
      `${issue.issue || 'Not specified'}\n` +
      (issue.issue_raised_by && issue.issue_raised_by !== "Not Available in the document" 
        ? `Raised by: ${issue.issue_raised_by}\n` : '') +
      (issue.issue_outcome ? `Outcome: ${issue.issue_outcome}\n` : '')
    )).join('\n');
  };

  const formatLegalPrinciples = (principles) => {
    if (!principles || principles.length === 0) return null;
    return principles.map((principle, i) => (
      `Principle ${i + 1}:\n` +
      `${principle.principle || 'Not specified'}\n` +
      (principle.principle_text ? `Text: ${principle.principle_text}\n` : '') +
      (principle.principle_source ? `Source: ${principle.principle_source}\n` : '')
    )).join('\n');
  };

  const formatLegalOutcomes = (outcomes) => {
    if (!outcomes || outcomes.length === 0) return null;
    return outcomes.map((outcome, i) => (
      `Outcome ${i + 1}:\n` +
      `${outcome.outcome || 'Not specified'}\n` +
      (outcome.outcome_date ? `Date: ${outcome.outcome_date}\n` : '') +
      (outcome.outcome_reason ? `Reason: ${outcome.outcome_reason}\n` : '')
    )).join('\n');
  };

  const formatLawsApplied = (laws) => {
    if (!laws || laws.length === 0) return null;
    return laws.map((law, i) => (
      `Law ${i + 1}:\n` +
      `${law.law || 'Not specified'}\n` +
      (law.law_section ? `Section: ${law.law_section}\n` : '') +
      (law.law_text ? `Text: ${law.law_text}\n` : '')
    )).join('\n');
  };

  const formatLegalPrecedents = (precedents) => {
    if (!precedents || precedents.length === 0) return null;
    return precedents.map((precedent, i) => (
      `Precedent ${i + 1}:\n` +
      `${precedent.precedent || 'Not specified'}\n` +
      (precedent.precedent_text ? `Text: ${precedent.precedent_text}\n` : '') +
      (precedent.precedent_source ? `Source: ${precedent.precedent_source}\n` : '')
    )).join('\n');
  };

  const formatPrayers = (prayers) => {
    if (!prayers || prayers.length === 0) return null;
    return prayers.map((prayer, i) => (
      `Prayer ${i + 1}:\n` +
      `${prayer.prayer || 'Not specified'}\n` +
      (prayer.prayer_type ? `Type: ${prayer.prayer_type}\n` : '') +
      (prayer.prayer_status ? `Status: ${prayer.prayer_status}\n` : '')
    )).join('\n');
  };

  const sections = [
    { 
      title: "Case Summary",
      content: formatCaseSummary(caseItem.case_summary),
      bgColor: 'bg-gray-50',
      defaultExpanded: true 
    },
    { 
      title: "Legal Issues",
      content: formatLegalIssues(caseItem.legal_issues),
      bgColor: 'bg-blue-50' 
    },
    { 
      title: "Legal Principles",
      content: formatLegalPrinciples(caseItem.legal_principles),
      bgColor: 'bg-green-50' 
    },
    { 
      title: "Legal Outcomes",
      content: formatLegalOutcomes(caseItem.legal_outcomes),
      bgColor: 'bg-yellow-50' 
    },
    { 
      title: "Laws Applied",
      content: formatLawsApplied(caseItem.laws_applied),
      bgColor: 'bg-purple-50' 
    },
    {
      title: "Legal Precedents",
      content: formatLegalPrecedents(caseItem.legal_precedents),
      bgColor: 'bg-red-50'
    },
    { 
      title: "Prayers",
      content: formatPrayers(caseItem.prayers),
      bgColor: 'bg-pink-50' 
    }
  ];

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200">
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-6 border-b border-blue-200 rounded-t-lg">
        <h3 className="text-xl font-bold text-indigo-900 mb-4">
          {caseItem.case?.metadata_citation || "Untitled Case"}
        </h3>
        <div className="grid grid-cols-2 gap-6">
          <div className="bg-white p-4 rounded-lg shadow-sm border border-blue-100">
            <h4 className="text-indigo-800 font-semibold flex items-center gap-2 mb-3">
              <span className="w-2 h-2 bg-indigo-500 rounded-full"/>
              Court Details
            </h4>
            <div className="space-y-2">
              <p className="text-sm">
                <span className="text-gray-600 font-medium">Court:</span>
                <span className="text-indigo-700 ml-2">{caseItem.case?.metadata_court || "N/A"}</span>
              </p>
              <p className="text-sm">
                <span className="text-gray-600 font-medium">County:</span>
                <span className="text-indigo-700 ml-2">{caseItem.case?.metadata_county || "N/A"}</span>
              </p>
              <p className="text-sm">
                <span className="text-gray-600 font-medium">Case Number:</span>
                <span className="text-indigo-700 ml-2">{caseItem.case?.metadata_case_number || "N/A"}</span>
              </p>
            </div>
          </div>
          
          <div className="bg-white p-4 rounded-lg shadow-sm border border-purple-100">
            <h4 className="text-purple-800 font-semibold flex items-center gap-2 mb-3">
              <span className="w-2 h-2 bg-purple-500 rounded-full"/>
              Important Dates
            </h4>
            <div className="space-y-2">
              <p className="text-sm">
                <span className="text-gray-600 font-medium">Date Filed:</span>
                <span className="text-purple-700 ml-2">{caseItem.case?.date_filed || "N/A"}</span>
              </p>
              <p className="text-sm">
                <span className="text-gray-600 font-medium">Date Delivered:</span>
                <span className="text-purple-700 ml-2">{caseItem.case?.metadata_date_delivered || "N/A"}</span>
              </p>
            </div>
          </div>

          <div className="bg-white p-4 rounded-lg shadow-sm border border-teal-100">
            <h4 className="text-teal-800 font-semibold flex items-center gap-2 mb-3">
              <span className="w-2 h-2 bg-teal-500 rounded-full"/>
              Legal Representatives
            </h4>
            <div className="space-y-2">
              <p className="text-sm">
                <span className="text-gray-600 font-medium">Judges:</span>
                <span className="text-teal-700 ml-2">{caseItem.case?.metadata_judges || "N/A"}</span>
              </p>
              <p className="text-sm">
                <span className="text-gray-600 font-medium">Advocates:</span>
                <span className="text-teal-700 ml-2">{caseItem.case?.metadata_advocate || "N/A"}</span>
              </p>
            </div>
          </div>

          <div className="bg-white p-4 rounded-lg shadow-sm border border-rose-100">
            <h4 className="text-rose-800 font-semibold flex items-center gap-2 mb-3">
              <span className="w-2 h-2 bg-rose-500 rounded-full"/>
              Parties
            </h4>
            <div className="space-y-2">
              <p className="text-sm text-rose-700">
                {caseItem.case?.metadata_parties || "N/A"}
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="p-4 space-y-4">
        {sections.map((section, index) => (
          <Section
            key={index}
            title={section.title}
            content={section.content}
            bgColor={section.bgColor}
            defaultExpanded={section.defaultExpanded}
          />
        ))}

        {caseItem.case?.metadata_doc_url && (
          <div className="flex gap-4 mt-4">
            <button
              onClick={() => onViewCase?.(caseItem.case.metadata_doc_url)}
              className="flex items-center gap-2 px-4 py-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
            >
              <ExternalLink className="h-4 w-4" />
              View in KLR
            </button>
            <button
              onClick={() => onDownloadCase?.(caseItem.case.metadata_doc_url)}
              className="flex items-center gap-2 px-4 py-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
            >
              <Download className="h-4 w-4" />
              Download
            </button>
          </div>
        )}
      </div>

      {caseItem.similarity !== undefined && (
        <div className="p-4 bg-gray-50 border-t border-gray-200">
          <span className="text-sm font-medium text-blue-600">
            {Math.round(caseItem.similarity * 100)}% Match
          </span>
        </div>
      )}
    </div>
  );
};

const RelatedCases = ({ cases = [], onViewCase, onDownloadCase }) => {
  if (!cases || cases.length === 0) {
    return (
      <div className="text-center py-6 text-gray-500 bg-white rounded-lg">
        No similar cases found
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <h2 className="text-xl font-bold text-gray-900 mb-4">Related Cases</h2>
      {cases.map((caseItem, index) => (
        <CaseCard 
          key={index} 
          caseItem={caseItem} 
          onViewCase={onViewCase} 
          onDownloadCase={onDownloadCase}
        />
      ))}
    </div>
  );
};

export default RelatedCases;